import React, { useMemo, useState, useEffect } from "react"
import { Container, CardBody, Card } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import { axiosbaseurl } from "configure"
import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import CommonTable from "components/Common/CommonTable"
import Loader from "components/Common/Loader"
import SetAutomatedEmail from "./SetAutomatedEmail"
import swal from "sweetalert"
import { setUpdatedData } from "helpers/data_setters"

const AutomatedReportsSummary = () => {
  const [table_data, settable_data] = useState([])
  const [loading, setloading] = useState(false)
  const [employeesMap, setEmployeesMap] = useState({}) // id → name map

  // Fetch all employees once to map IDs → names
  const fetchEmployees = async () => {
    try {
      const res = await axiosbaseurl.get("/employee-master")
      const empList = res.data?.data || []
      const map = {}
      empList.forEach(emp => {
        map[emp.id] = `${emp.name} ${emp.lastName || ""}`.trim()
      })
      setEmployeesMap(map)
    } catch (error) {
      console.error("Failed to fetch employees:", error)
      swal("", "Failed to load employee names", "error")
    }
  }

  const getAllAutomatedMails = async () => {
    setloading(true)
    try {
      const response = await axiosbaseurl.get(`/automated/email/reports`)
      if (response.data.success) {
        settable_data(response.data.data || [])
      }
    } catch (error) {
      console.log(error)
      settable_data([])
    } finally {
      setloading(false)
    }
  }

  useEffect(() => {
    fetchEmployees() // Fetch employees once on mount
    getAllAutomatedMails()
  }, [])

  document.title = "Automated Email Reports | Peacock Accountants Limited"

  return (
    <>
      {loading && <Loader />}
      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb
            title={"Dashboard"}
            breadcrumbItem={"Automated Email Reports"}
          />
          <Card className="px-0 mx-0">
            <CardBody>
              <TableComponent
                table_data={table_data}
                settable_data={settable_data}
                employeesMap={employeesMap} // pass the map to table
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

const TableComponent = ({ table_data, settable_data, employeesMap }) => {
  const [addShow, setAddShow] = useState(false)
  const [template_id, settemplate_id] = useState(null)
  const { checkAccess } = usePermissionData()
  const module_id = 24
  const navigate = useNavigate()

  const handleStatus = async (status, id) => {
    try {
      if (status) {
        const response = await axiosbaseurl.patch(
          `/automated/email/reports/${id}/status`,
          { status }
        )
        if (response.data.success) {
          settable_data(prev =>
            setUpdatedData(prev, response.data.data, "edit", "Status ")
          )
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  const handleDelete = async id => {
    swal({
      title: "Are you sure ?",
      text: "You want to delete this automated email report",
      icon: "warning",
      buttons: ["Cancel", "Yes, Delete"],
      dangerMode: true,
    }).then(async willDelete => {
      if (willDelete) {
        try {
          const response = await axiosbaseurl.delete(
            `/automated/email/reports/${id}`
          )

          if (response.data.success) {
            settable_data(prev =>
              setUpdatedData(prev, response.data.data, "delete")
            )
            swal("Deleted!", "Report deleted successfully.", "success")
          }
        } catch (error) {
          console.error(error)
          swal(
            "Failed!",
            error.response?.data?.message || "Failed to delete report.",
            "error"
          )
        }
      }
    })
  }
  // Badge helper function
  const renderBadges = (items = [], className = "bg-primary") => {
    // Ensure items is always an array
    const safeItems = Array.isArray(items) ? items : []

    return (
      <div className="d-flex flex-wrap gap-1">
        {safeItems.length > 0 ? (
          safeItems.map((item, idx) => (
            <span
              key={idx}
              className={`badge ${className} px-2 py-1`}
              style={{ fontSize: "0.85rem" }}
            >
              {item}
            </span>
          ))
        ) : (
          <span className="text-muted">—</span>
        )}
      </div>
    )
  }

  // columns
  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        disableFilters: true,
        className: "text-center",
        Cell: cellProps => <span>{cellProps.row.index + 1}</span>,
      },
      {
        Header: "Report(s)",
        accessor: "reports",
        className: "text-center",
        disableFilters: true,
        Cell: cellProps =>
          renderBadges(
            Array.isArray(cellProps.value) ? cellProps.value : [],
            "bg-info"
          ),
      },
      {
        Header: "Employee(s)",
        accessor: "employees",
        className: "text-center",
        disableFilters: true,
        Cell: cellProps => {
          const employeeIds = Array.isArray(cellProps.value)
            ? cellProps.value
            : []
          const names = employeeIds.map(id => employeesMap[id] || `ID: ${id}`)
          return renderBadges(names, "bg-success")
        },
      },
      {
        Header: "Scheduled Day(s)",
        accessor: "days",
        disableFilters: true,
        className: "text-center",
        Cell: cellProps =>
          renderBadges(
            Array.isArray(cellProps.value) ? cellProps.value : [],
            "bg-light text-dark "
          ),
      },
      {
        Header: "Scheduled Time (UK)",
        accessor: "time",
        disableFilters: true,
        className: "text-center",
      },
      {
        Header: "Status",
        disableSort: true,
        accessor: "status",
        disableFilters: true,
        className: "text-center",
        Cell: cellProps => {
          const status = cellProps.row.original.status
          return (
            <div className="text-center">
              <select
                onChange={e =>
                  handleStatus(e.target.value, cellProps.row.original.id)
                }
                disabled={!checkAccess(module_id, "edit")}
                className={`badge fs-6 border-0 status_select_dropdown 
                  bg-${status === "Active" ? "success" : "danger"}`}
                value={status}
              >
                <option value="Active">Active</option>
                <option value="InActive">InActive</option>
              </select>
            </div>
          )
        },
      },
      {
        Header: "Action",
        disableFilters: true,
        disableSort: true,
        Cell: cellProps => (
          <div className="d-flex justify-content-center">
            <button
              onClick={() => {
                settemplate_id(cellProps.row.original.id)
                setAddShow(true)
              }}
              className="btn btn-success bg-green btn-sm mx-2"
            >
              <i
                className={`fas fa-${
                  checkAccess(module_id, "edit") ? "pen" : "eye"
                }`}
              />
            </button>
            <button
              onClick={() => handleDelete(cellProps.row.original.id)}
              className="btn btn-danger btn-sm"
            >
              <i className="fas fa-trash-alt" title="Delete" />
            </button>
          </div>
        ),
      },
    ],
    [employeesMap, checkAccess] // re-create columns when employeesMap changes
  )

  const data = useMemo(() => table_data, [table_data])

  return (
    <>
      <PermittedComponent isAccess={checkAccess(module_id, "view")}>
        <CommonTable
          columns={columns}
          data={data}
          heading={
            <>
              <h5 className="fw-bold text-primary">Automated Email Reports</h5>
              <h6 className="fw-medium">
                Mail report(s) to employee(s) at scheduled time & day(s)
              </h6>
            </>
          }
        >
          <div>
            <button
              onClick={() => {
                settemplate_id(null)
                setAddShow(true)
              }}
              className="btn btn-primary bg-blue ms-3"
            >
              <i className="fas fa-plus me-2"></i>
              Set Automated Email
            </button>
            <button
              onClick={() => navigate(-1)}
              className="btn btn-secondary bg-dark ms-3"
            >
              <i className="fas fa-arrow-left me-3"></i>
              Back
            </button>
          </div>
        </CommonTable>

        <SetAutomatedEmail
          show={addShow}
          setShow={setAddShow}
          settable_data={settable_data}
          template_id={template_id}
          settemplate_id={settemplate_id}
        />
      </PermittedComponent>
    </>
  )
}

export default AutomatedReportsSummary
